import { __makeTemplateObject } from '../../node_modules/tslib/tslib.es6.js';
import React from 'react';
import { Box, themeGet, Spinner, Text } from '@primer/react';
import styled from 'styled-components';
import { truncateTextFromMiddle } from '../../utils/functions.js';
import LTCheckbox from '../LTCheckbox/index.js';
import LTCounterLabel from '../LTCounterLabel/index.js';
import LTPopover from '../LTPopover/index.js';
import LTTag from '../LTTags/index.js';
import LTTooltip from '../LTTooltip/index.js';

var BuildCardWrapper = styled(Box)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  border: 1px solid ", ";\n  border-radius: 8px;\n  display: flex;\n  gap: ", ";\n  padding: 12px 8px;\n  width: 100%;\n  cursor: pointer;\n  transition: background-color 0.3s ease;\n  &:hover {\n    background-color: #dddddd33;\n  }\n  &:focus-visible {\n    outline-color: #101010;\n  }\n"], ["\n  border: 1px solid ", ";\n  border-radius: 8px;\n  display: flex;\n  gap: ", ";\n  padding: 12px 8px;\n  width: 100%;\n  cursor: pointer;\n  transition: background-color 0.3s ease;\n  &:hover {\n    background-color: #dddddd33;\n  }\n  &:focus-visible {\n    outline-color: #101010;\n  }\n"])), themeGet("colors.border.default"), themeGet("space.2"));
var BuildCardButton = styled(Box)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  position: absolute;\n  width: 100%;\n  height: 100%;\n  top: 0;\n  left: 0;\n  &:focus-visible {\n    outline-color: #101010;\n  }\n"], ["\n  position: absolute;\n  width: 100%;\n  height: 100%;\n  top: 0;\n  left: 0;\n  &:focus-visible {\n    outline-color: #101010;\n  }\n"])));
var InfoList = styled.ul(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  list-style-type: none;\n  display: flex;\n  align-items: center;\n  gap: ", ";\n  padding: 0px;\n  margin: 0px;\n  @media only screen and (max-width: 1024px) {\n    flex-wrap: wrap;\n  }\n"], ["\n  list-style-type: none;\n  display: flex;\n  align-items: center;\n  gap: ", ";\n  padding: 0px;\n  margin: 0px;\n  @media only screen and (max-width: 1024px) {\n    flex-wrap: wrap;\n  }\n"])), themeGet("space.2"));
var Item = styled.li(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  gap: ", ";\n"], ["\n  display: flex;\n  align-items: center;\n  gap: ", ";\n"])), themeGet("space.2"));
var Dot = styled.span(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  display: inline-block;\n  width: 4px;\n  height: 4px;\n  border-radius: 50%;\n  background-color: ", ";\n"], ["\n  display: inline-block;\n  width: 4px;\n  height: 4px;\n  border-radius: 50%;\n  background-color: ", ";\n"])), themeGet("colors.fg.disabled"));
var OLD_HYPEREXCUTE_BUILD_TYPE = "hypertest";
var ToolTipTag = function ToolTipTag(_a) {
  var tags = _a.tags,
    totalCount = _a.totalCount;
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: "flex",
      gap: themeGet("space.2"),
      alignItems: "start",
      flexDirection: "column"
    }
  }, tags.slice(2, totalCount).map(function (tag, index) {
    var _a = (tag || {}).name,
      name = _a === void 0 ? "" : _a;
    return /*#__PURE__*/React.createElement(LTTooltip, {
      text: name,
      key: "".concat(name, "-").concat(index)
    }, /*#__PURE__*/React.createElement(LTTag, {
      text: name
    }));
  }));
};
/**
 * PropTypes for the `LTBuildCard` component, defining the expected properties and their types.
 * This component represents a build card that displays information about a specific build,
 * including its type, name, creation date, associated user, and other related attributes.
 *
 * @param {string} ariaLabelForCheckbox - Aria label for the checkbox, if any, for accessibility purposes.
 * @param {string} buildType - The type of the build (e.g., "Development", "Production").
 * @param {string} buildTypeImg - The URL or path to an image representing the build type.
 * @param {string} buildName - The name of the build.
 * @param {Object} buildCardRef - A React ref object pointing to the build card element.
 * @param {string} createdAt - The creation date of the build.
 * @param {string} username - The username of the user who initiated the build.
 * @param {string} testTypeImg - The URL or path to an image representing the type of test(s) associated with the build.
 * @param {number} totalTests - The total number of tests associated with the build.
 * @param {boolean} isTestRunning - Indicates if the build's tests are currently running.
 * @param {boolean} isSelectedBuild - Indicates if the build is currently selected.
 * @param {boolean} isSampleBuild - Indicates if the build is a sample build.
 * @param {Function} onClick - Callback function to be invoked when the build card is clicked.
 * @param {Function} onKeyDown - Callback function to be invoked when a keydown event occurs on the build card.
 * @param {boolean} showCheckbox - Determines whether a checkbox should be shown on the build card.
 * @param {string} taskIcon - The URL or path to an icon representing the build's task.
 * @param {string} tooltip - Tooltip text providing additional information about the build.
 * @param {Array} tags - An array of tags or keywords associated with the build.
 */
var LTBuildCard = function LTBuildCard(_a) {
  var _b = _a.ariaLabelForCheckbox,
    ariaLabelForCheckbox = _b === void 0 ? undefined : _b,
    _c = _a.buildType,
    buildType = _c === void 0 ? "" : _c,
    _d = _a.buildTypeImg,
    buildTypeImg = _d === void 0 ? "" : _d,
    buildCardRef = _a.buildCardRef,
    _e = _a.buildName,
    buildName = _e === void 0 ? "" : _e,
    _f = _a.buildId,
    buildId = _f === void 0 ? "" : _f,
    _g = _a.createdAt,
    createdAt = _g === void 0 ? "" : _g,
    _h = _a.onClick,
    _onClick = _h === void 0 ? function () {} : _h,
    _j = _a.onChange,
    _onChange = _j === void 0 ? function () {} : _j,
    _k = _a.onKeyDown,
    onKeyDown = _k === void 0 ? function () {} : _k,
    _l = _a.isCheckboxChecked,
    isCheckboxChecked = _l === void 0 ? function () {} : _l,
    _m = _a.isSelectedBuild,
    isSelectedBuild = _m === void 0 ? false : _m,
    _o = _a.isTestRunning,
    isTestRunning = _o === void 0 ? false : _o,
    _p = _a.buildIndex,
    buildIndex = _p === void 0 ? 0 : _p,
    _q = _a.isSampleBuild,
    isSampleBuild = _q === void 0 ? false : _q,
    _r = _a.showCheckbox,
    showCheckbox = _r === void 0 ? false : _r,
    _s = _a.taskIcon,
    taskIcon = _s === void 0 ? "" : _s,
    _t = _a.tags,
    tags = _t === void 0 ? [] : _t,
    _u = _a.testTypeImg,
    testTypeImg = _u === void 0 ? "" : _u,
    _v = _a.tooltip,
    tooltip = _v === void 0 ? "" : _v,
    _w = _a.totalTests,
    totalTests = _w === void 0 ? 0 : _w,
    _x = _a.username,
    username = _x === void 0 ? "" : _x;
  var truncatedBuildName = truncateTextFromMiddle(buildName, 60);
  var truncatedUsername = truncateTextFromMiddle(username, 20);
  var totalTags = Array.isArray(tags) ? Number(tags.length) : 0;
  var remainingTagCount = Array.isArray(tags) ? Number(totalTags) - 2 : 0;
  var displayedTagsText = Array.isArray(tags) && tags.slice(0, 2) // Take only the first two elements
  .map(function (obj) {
    return obj === null || obj === void 0 ? void 0 : obj.name;
  }).join(" ");
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BuildCardWrapper, {
    sx: {
      backgroundColor: isSelectedBuild ? "#DDDDDD66" : {},
      position: "relative"
    }
  }, /*#__PURE__*/React.createElement(BuildCardButton, {
    "aria-pressed": isSelectedBuild,
    "aria-label": "".concat(buildName, ", ").concat(displayedTagsText ? "Tags - ".concat(displayedTagsText) : "", " ").concat(remainingTagCount > 0 ? "+".concat(remainingTagCount, " tags,") : "", " total ").concat(totalTests, " tests, by ").concat(username, ", created ").concat(createdAt, ", Build Status-").concat(tooltip, ", Build Type ").concat(buildType, ", Press Enter to view"),
    onClick: _onClick,
    onKeyDown: onKeyDown,
    role: "button",
    ref: function ref(el) {
      if (buildCardRef) {
        buildCardRef.current[buildIndex] = el;
      }
    },
    tabIndex: 0
  }), /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: "inline-flex",
      height: "fit-content",
      mt: showCheckbox ? 0 : 3
    },
    "aria-hidden": !showCheckbox ? true : false,
    onClick: function onClick(e) {
      if (!showCheckbox) {
        e.stopPropagation();
        _onClick(e);
      }
    }
  }, showCheckbox ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(LTCheckbox, {
    ariaLabel: ariaLabelForCheckbox,
    checked: isCheckboxChecked(buildId),
    onChange: function onChange(value) {
      _onChange(value, buildId);
    },
    label: "Press space to toggle delete build ".concat(buildId),
    visuallyHidden: true,
    onClick: function onClick(e) {
      return e.stopPropagation();
    },
    onKeyDown: function onKeyDown(e) {
      return e.stopPropagation();
    }
  })) : /*#__PURE__*/React.createElement(LTTooltip, {
    direction: LTTooltip.Direction.RIGHT,
    text: tooltip,
    wrap: true
  }, isTestRunning ? /*#__PURE__*/React.createElement(Spinner, {
    size: "small",
    sx: {
      color: "#0366D6",
      fill: "#0366D6",
      fillOpacity: "0.1"
    }
  }) : /*#__PURE__*/React.createElement("img", {
    src: testTypeImg,
    alt: tooltip,
    height: "16",
    width: "16"
  }))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      width: "100%"
    }
  }, /*#__PURE__*/React.createElement(Box, {
    className: "buildListCard",
    sx: {
      display: "inline-flex"
    },
    onClick: function onClick(e) {
      e.stopPropagation();
      _onClick(e);
    }
  }, /*#__PURE__*/React.createElement(LTTooltip, {
    direction: LTTooltip.Direction.BOTTOM,
    text: buildName,
    wrap: true
  }, /*#__PURE__*/React.createElement(Text, {
    as: "h4",
    "aria-hidden": "true",
    sx: {
      color: "fg.default",
      fontWeight: 600,
      lineHeight: "20px",
      marginTop: 0,
      marginBottom: 0,
      wordBreak: "break-all"
    }
  }, truncatedBuildName))), tags && tags.length > 0 && /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: "flex",
      gap: "8px"
    },
    onClick: function onClick(e) {
      e.stopPropagation();
      _onClick(e);
    }
  }, /*#__PURE__*/React.createElement(InfoList, null, tags.slice(0, 2).map(function (tag, index) {
    var _a = (tag || {}).name,
      name = _a === void 0 ? "" : _a;
    return /*#__PURE__*/React.createElement(Item, {
      "aria-hidden": true
    }, /*#__PURE__*/React.createElement(LTTooltip, {
      text: name,
      key: "".concat(name, "-").concat(index)
    }, /*#__PURE__*/React.createElement(LTTag, {
      text: name
    })));
  })), remainingTagCount > 0 && /*#__PURE__*/React.createElement(LTPopover, {
    ariaDescribedBy: "lt-build-card-popover-".concat(buildIndex),
    text: /*#__PURE__*/React.createElement(ToolTipTag, {
      totalCount: totalTags,
      tags: tags
    })
  }, /*#__PURE__*/React.createElement(LTCounterLabel, {
    count: "+".concat(remainingTagCount),
    scheme: LTCounterLabel.Scheme.OUTLINED
  }))), /*#__PURE__*/React.createElement(InfoList, {
    "aria-hidden": "true",
    onClick: function onClick(e) {
      e.stopPropagation();
      _onClick(e);
    }
  }, /*#__PURE__*/React.createElement(Item, null, /*#__PURE__*/React.createElement(LTTooltip, {
    direction: LTTooltip.Direction.BOTTOM,
    text: "Total Tests",
    wrap: true
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: "flex",
      alignItems: "center",
      gap: "4px"
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: taskIcon,
    alt: "task"
  }), /*#__PURE__*/React.createElement(Text, {
    sx: {
      color: "fg.muted"
    }
  }, totalTests)))), /*#__PURE__*/React.createElement(Item, null, /*#__PURE__*/React.createElement(Dot, null), /*#__PURE__*/React.createElement(LTTooltip, {
    direction: LTTooltip.Direction.BOTTOM,
    text: "Time Since",
    wrap: true
  }, !isSampleBuild && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    sx: {
      color: "fg.muted"
    }
  }, createdAt)))), /*#__PURE__*/React.createElement(Item, null, /*#__PURE__*/React.createElement(Dot, null), /*#__PURE__*/React.createElement(LTTooltip, {
    direction: LTTooltip.Direction.BOTTOM,
    text: username.length > 20 ? "".concat(username) : "Username",
    wrap: true
  }, /*#__PURE__*/React.createElement(Text, {
    sx: {
      color: "fg.muted"
    }
  }, truncatedUsername))), /*#__PURE__*/React.createElement(Item, null, /*#__PURE__*/React.createElement(Dot, null), /*#__PURE__*/React.createElement(LTTooltip, {
    direction: LTTooltip.Direction.LEFT,
    text: buildType === OLD_HYPEREXCUTE_BUILD_TYPE ? "HyperExecute" : buildType,
    wrap: true
  }, /*#__PURE__*/React.createElement("img", {
    src: buildTypeImg,
    alt: buildType === OLD_HYPEREXCUTE_BUILD_TYPE ? "HyperExecute" : buildType,
    height: "20",
    width: "20"
  })))))));
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5;

export { LTBuildCard as default };
